// Types
import type { FormEvent } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import Link from 'next/link';

import uniq from 'lodash/uniq';
import { matchSorter } from 'match-sorter';

// Hooks
import useFormatSocialProfilesNetwork from '@/hooks/format/useFormatSocialProfilesNetwork';
import useErrorTranslation from '@/hooks/language/useErrorTranslation';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useTeamMembers from '@/hooks/swr/useTeamMembers';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useToggle from '@/hooks/utils/useToggle';

import type { DashboardType } from '@/types/dashboardType';
import type { SocialProfileType } from '@/types/socialProfileType';
import type { TeamMemberType } from '@/types/teamMemberType';

//routes
import { USERS_ROLES } from '@/lib/routes/privateRoutes';

import Button from '@/components/base/Button';
import Checkbox from '@/components/base/Checkbox';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
// Components
import Modal from '@/components/base/Modal';
import ActionTag from '@/components/base/Tag/ActionTag';
import Tooltip from '@/components/base/Tooltip';
import { SuccessModal } from '@/components/pages/custom-dashboards/modals/SuccessModal';
import { Trans } from '@/components/tools/translation';

import { SHARE_DASHBOARD } from '@/routes/apiEndpoints';

type SocialProfilesItemProps = {
    user: TeamMemberType & { missingSps?: SocialProfileType['id'][] };
    isSubmitting: boolean;
    onCheck: SocialProfilesItemsProps['onCheck'];
    selectedIds: SocialProfileType['id'][];
    isAllowed?: boolean;
};

export const SocialProfilesItem = ({
    user,
    isSubmitting,
    onCheck,
    selectedIds,
    isAllowed = true
}: SocialProfilesItemProps) => {
    const { t } = useTranslation('commons');
    const { formatSocialProfiles } = useFormatSocialProfilesNetwork();
    const { socialProfiles } = useSocialProfiles({
        revalidateIfStale: false
    });

    const isSelected = useMemo(() => {
        const element = selectedIds.find((id) => user.id === id);
        return !!element;
    }, [selectedIds, user.id]);
    const missingSps = useMemo(() => {
        const missingSps = socialProfiles?.filter(
            (social_profile) => user?.missingSps?.includes(social_profile?.id)
        );
        return missingSps?.map(
            (sp) => `${sp?.slug} (${formatSocialProfiles(sp?.social_network_type)})`
        );
    }, [formatSocialProfiles, socialProfiles, user.missingSps]);

    return (
        <div className="flex items-center justify-between">
            <div className="text-left">
                <span className="block font-GSemiBold text-sm">{`${user.first_name} ${user.last_name}`}</span>
                <span className="block font-GMedium text-xs text-icoGray-400">{user.email}</span>
            </div>
            <div className="flex items-center">
                {isAllowed ? (
                    <ActionTag variant="role" role={user.acl_role}>
                        {t(`roles.${user.acl_role}`)}
                    </ActionTag>
                ) : (
                    <Tooltip
                        interactive
                        content={
                            <Trans
                                i18nKey="dashboard:share_dashboard_not_authorized_tooltip"
                                values={{
                                    users: missingSps.join(', ')
                                }}
                                components={{
                                    tag0: (
                                        <Link
                                            className="text-icoBlue underline"
                                            href={USERS_ROLES}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        }>
                        <div className="pl-14 text-right text-xs text-icoGray-400 dark:text-icoDarkMode-beton">
                            <Trans
                                i18nKey="dashboard:share_dashboard_not_authorized_content"
                                values={{
                                    users: `${missingSps[0]}${
                                        user.missingSps.length > 1 ? ', ...' : ''
                                    }`
                                }}
                            />
                        </div>
                    </Tooltip>
                )}

                <Checkbox
                    className="ml-8"
                    aria-label={user.last_name}
                    id={user.id.toString()}
                    onChange={() => onCheck(user.id)}
                    checked={isAllowed ? isSelected ?? false : false}
                    disabled={isSubmitting || !isAllowed}
                />
            </div>
        </div>
    );
};

type SocialProfilesItemsProps = {
    listUsers: TeamMemberType[];
    isSubmitting: boolean;
    checkUncheckAll: (users: TeamMemberType[]) => void;
    onCheck: (id_user: TeamMemberType['id']) => void;
    selectedIds: SocialProfileType['id'][];
    dashboardSocialProfileIds: SocialProfileType['id'][];
    dashboardSpGroupIds: SpGroupType['id'][];
};

export const SocialProfilesItems = ({
    listUsers,
    isSubmitting,
    checkUncheckAll,
    onCheck,
    selectedIds,
    dashboardSocialProfileIds,
    dashboardSpGroupIds
}: SocialProfilesItemsProps) => {
    const { t } = useTranslation('dashboard');
    const [searchValue, setSearchValue] = useState('');

    const { spGroups } = useSPGroups({
        revalidateOnFocus: false,
        revalidateOnMount: false,
        revalidateOnReconnect: false,
        shouldRetryOnError: false
    });

    // Function to filter users on input value
    const getUsers = useCallback(
        (filter: string) => {
            if (!filter) {
                return listUsers;
            }
            return matchSorter(listUsers, filter, {
                keys: [
                    { threshold: matchSorter.rankings.CONTAINS, key: 'first_name' },
                    { threshold: matchSorter.rankings.CONTAINS, key: 'last_name' },
                    { threshold: matchSorter.rankings.CONTAINS, key: 'email' }
                ]
            });
        },
        [listUsers]
    );

    const allUsers = useMemo(() => getUsers(searchValue), [searchValue, getUsers]);

    const thisUserHasPermission = useCallback(
        (user: TeamMemberType) => {
            const missingSPs = [];

            const userSocialProfileIds = user.social_profiles.map((sp) => sp.id);

            dashboardSpGroupIds.forEach((dashboardSpGroupId) => {
                const spGroup = spGroups?.find((spGroup) => spGroup.id === dashboardSpGroupId);

                if (spGroup) {
                    spGroup.social_profiles.forEach((spGroupSocialProfile) => {
                        if (!userSocialProfileIds.includes(spGroupSocialProfile.id)) {
                            missingSPs.push(spGroupSocialProfile.id);
                        }
                    });
                }
            });

            dashboardSocialProfileIds.forEach((dashboardSocialProfileId) => {
                if (!userSocialProfileIds.includes(dashboardSocialProfileId)) {
                    missingSPs.push(dashboardSocialProfileId);
                }
            });

            return uniq(missingSPs);
        },
        [dashboardSocialProfileIds, dashboardSpGroupIds, spGroups]
    );

    const usersPermission = useMemo(() => {
        const notAllowedProfiles = [];
        const allowedProfiles = [];

        allUsers?.map((user) => {
            const hasMissingSps = thisUserHasPermission(user);

            if (hasMissingSps.length) {
                notAllowedProfiles.push({
                    ...user,
                    missingSps: hasMissingSps
                });
            } else {
                allowedProfiles.push(user);
            }
        });

        return { notAllowedProfiles, allowedProfiles };
    }, [allUsers, thisUserHasPermission]);

    return (
        <>
            <Input
                id="search-profile"
                inputStyle="datepicker"
                noBorder={true}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('dashboard_search_users')}
                beforeInput={<Icon icon="search" />}
                afterInput={
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => checkUncheckAll(usersPermission.allowedProfiles)}
                        className="hidden font-GSemiBold text-xs text-icoBlue sm:block"
                        data-testid="select-all">
                        {selectedIds.length === usersPermission.allowedProfiles.length
                            ? t('commons:unselect_all')
                            : t('commons:select_all')}
                    </button>
                }
            />
            <div className="scrollbar mt-8 flex max-h-56 flex-col space-y-5 pr-1">
                {usersPermission.allowedProfiles.length > 0 &&
                    usersPermission.allowedProfiles.map((user) => (
                        <SocialProfilesItem
                            user={user}
                            key={user.id}
                            isSubmitting={isSubmitting}
                            onCheck={onCheck}
                            selectedIds={selectedIds}
                        />
                    ))}
                {usersPermission.notAllowedProfiles.length > 0 && (
                    <>
                        <div className="rounded-m bg-icoGray-300 p-2 text-icoGray-500 dark:bg-icoDarkMode-anthracite dark:text-white">
                            {t('share_dashboard_not_authorized_title')}
                        </div>
                        {usersPermission.notAllowedProfiles.map((user) => (
                            <SocialProfilesItem
                                user={user}
                                key={user.id}
                                isSubmitting={isSubmitting}
                                onCheck={onCheck}
                                selectedIds={selectedIds}
                                isAllowed={false}
                            />
                        ))}
                    </>
                )}
            </div>
        </>
    );
};

type ShareModalsProps = {
    isOpen: boolean;
    toggle: () => void;
    dashboard: DashboardType;
};

export const ShareModals = ({ isOpen, toggle, dashboard }: ShareModalsProps) => {
    const { t } = useTranslation('dashboard');
    const { t_error } = useErrorTranslation();
    const { otherMembersOnly, isLoading } = useTeamMembers({
        revalidateOnFocus: false
    });
    const { isOpen: isOpenSuccessSPModal, toggle: toggleSuccessSPModal } = useToggle({
        initial: false
    });
    const [formState, setFormState] = useState({
        isSubmitting: false,
        error: null
    });
    const [selectedIds, setSelectedIds] = useState<TeamMemberType['id'][]>([]);

    const handleSubmissionError = () => {
        setFormState((prevState) => ({
            ...prevState,
            isSubmitting: false,
            error: t('errors:error_occured_submit_data')
        }));
    };

    const handleValidationErrors = (
        errorsFields: Record<'recipients' | `recipients.${string}`, string[]>
    ) => {
        const usersError = [];
        Object.keys(errorsFields).forEach((field) => {
            const error_type = errorsFields[field][0];

            if (field.includes('recipients.')) {
                const idUserError = field.split('.')[1];
                const usernameError = otherMembersOnly?.find(
                    (user) => user.id === Number(idUserError)
                );

                if (usernameError) {
                    usersError.push(usernameError.email);
                }
            } else {
                setFormState((prevState) => ({
                    ...prevState,
                    isSubmitting: false,
                    error: t_error('errors:error_dashboard.error_' + field + '_' + error_type)
                }));
            }
        });

        if (usersError?.length) {
            setFormState((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: (
                    <Trans
                        i18nKey="errors:error_dashboard.recipients_unique"
                        values={{ users: usersError.join(', ') }}
                    />
                )
            }));
        } else {
            handleSubmissionError();
        }
    };

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormState((prevState) => ({
            ...prevState,
            error: null,
            isSubmitting: true
        }));

        try {
            await post(injectEndpointUrlParams(SHARE_DASHBOARD, { dashboardId: dashboard.id }), {
                recipients: selectedIds
            });
            setFormState((prevState) => ({
                ...prevState,
                isSubmitting: false
            }));
            toggle();
            toggleSuccessSPModal();
        } catch (e) {
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors as Record<
                    'recipients' | `recipients.${string}`,
                    string[]
                >;
                // Parse error object, get first error and display errors under each field
                if (errorsFields) {
                    handleValidationErrors(errorsFields);
                }
            } else {
                handleSubmissionError();
            }
        }
    };

    const checkUncheckAll = (users: TeamMemberType[]) => {
        if (selectedIds.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(() => {
                const ids = [];
                users.forEach((user) => {
                    ids.push(user.id);
                });
                return ids;
            });
        }
    };

    const onCheckUser = (id_user: TeamMemberType['id']) => {
        if (selectedIds.find((id) => id === id_user)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== id_user));
        } else {
            setSelectedIds((prevState) => [...prevState, id_user]);
        }
    };

    return (
        <>
            <Modal open={isOpen} onDismiss={toggle}>
                <Modal.Body>
                    <Modal.Title>
                        {t('dashboard:share_dashboard_title')}
                        <p className="pt-5 font-GMedium text-base text-icoGray-500">
                            {t('dashboard:share_dashboard_notice')}
                        </p>
                    </Modal.Title>
                    <Modal.Content className="pt-8 text-center text-sm">
                        {formState.error && (
                            <InfoBanner
                                className="my-4 w-auto"
                                title={t('errors:error')}
                                message={formState.error}
                                onClose={() =>
                                    setFormState((prevState) => ({
                                        ...prevState,
                                        error: null
                                    }))
                                }
                            />
                        )}
                        <form onSubmit={onSubmit}>
                            <div className="rounded-md bg-icoGray-100 p-8 dark:bg-icoDarkMode-deepBlack">
                                {isLoading ? (
                                    <div className="h-12 w-full animate-pulse bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                                        <span className="h-full rounded-md bg-gray-100 dark:bg-icoDarkMode-deepBlack" />
                                    </div>
                                ) : (
                                    <SocialProfilesItems
                                        listUsers={otherMembersOnly}
                                        isSubmitting={formState.isSubmitting}
                                        checkUncheckAll={checkUncheckAll}
                                        selectedIds={selectedIds}
                                        onCheck={onCheckUser}
                                        dashboardSocialProfileIds={dashboard.data.social_profiles}
                                        dashboardSpGroupIds={dashboard.data?.groups ?? []}
                                    />
                                )}
                            </div>
                            <div className="mt-8 flex items-center justify-between pb-12">
                                <button
                                    type="button"
                                    onClick={toggle}
                                    className="font-GSemiBold text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf">
                                    {t('commons:cancel')}
                                </button>
                                <Button
                                    type="submit"
                                    disabled={selectedIds.length === 0}
                                    isLoading={formState.isSubmitting}>
                                    {t('commons:confirm')}
                                </Button>
                            </div>
                        </form>
                    </Modal.Content>
                </Modal.Body>
            </Modal>
            <SuccessModal
                notice={t('dashboard:share_dashboard_success_notice')}
                isOpen={isOpenSuccessSPModal}
                toggle={toggleSuccessSPModal}
                buttonText={t('commons:close')}
            />
        </>
    );
};
